import { getTeamsBySeason, getTeamStatisticsBySeason, getGoalTeamsBySeason, getSeasonsByTeam } from '@/utils/api/teams';
import { filterByCategoryType } from '@/utils/playsFiltering';
import teamGoalPlayIndexes from '@/data/team/teamGoalPlayIndexes';
import getTeamsStatPlayIndexes from '@/data/team/teamStatsPlayIndexes';
import plays from '@/data/plays';
import axios from 'axios';
const teamPlayIndexes = getTeamsStatPlayIndexes();

const state = {
  team: {},
  seasonTeams: [],
  seasonTeamsAnalysis: [],
  teamSeasons: {},
  gamesBySeason: [],
  plays: plays.plays,
  lastSeasonForSeasonTeamsAnalysis: 0,
  teamPlaysByPlayer: new Map(),
  formattedSeasons: {},
  loadCount: 0,
};

const mutations = {
  setLoadCount(state, loadCount) {
    state.loadCount = loadCount;
  },
  setFormattedSeasons(state, formattedSeasons) {
    state.formattedSeasons = formattedSeasons;
  },
  updateTeam(state, newTeam) {
    state.team = newTeam;
  },
  updateTeams(state, newTeams) {
    state.seasonTeams = newTeams;
  },
  updateGames(state, newGames) {
    state.gamesBySeason = newGames;
  },
  updateTeamsForAnalysis(state, newTeams) {
    state.seasonTeamsAnalysis = newTeams;
  },
  updateLastSeasonForSeasonTeamsAnalysis(state, seasonId) {
    state.lastSeasonForSeasonTeamsAnalysis = seasonId;
  },
  setTeamPlaysByPlayer(state, playsByPlayer) {
    state.teamPlaysByPlayer = playsByPlayer;
  },
};

const actions = {
  /**
   * @author Alejandro
   * @description This action is the action called from the view Team.vue when it's created
   * @param {Object} context
   * @param {number} teamId - The team id provided by the url teamsAnalysis/:teamId
   * @return {Promise<void>}
   */
  async checkState({ commit, dispatch, rootGetters }, teamId) {
    try {
      let seasonSelected = window.localStorage.getItem('seasonSelected');
      if (seasonSelected) {
        seasonSelected = Number(seasonSelected);
      }
      const promises = [];
      commit('setIsSectionLoading', true, { root: true });
      commit('updateTeam', { team_id: teamId });
      commit('filterGeneral/setSeasonId', seasonSelected || rootGetters['loginGeneral/seasonId'], { root: true });
      promises.push(getSeasonsByTeam({ teamId: teamId }));
      await dispatch('fillStatisticData', promises);
    } catch (e) {
      // commit('setIsSectionLoading', false, { root: true });
      throw new Error(e);
    }
  },
  /**
   * @author Alejandro
   * @description This function fill the statistic data for a team, is triggered by the action 'checkState'
   * or by the view Team.vue when a season changes
   * @param dispatch
   * @param commit
   * @param state
   * @param rootGetters
   * @param rootState
   * @param {Array<Promise>} promises
   * @return {Promise<never>}
   */
  async fillStatisticData({ dispatch, commit, state, rootGetters, rootState }, promises = [undefined]) {
    try {
      state.loadCount += 1;
      if (!rootState.isSectionLoading) {
        commit('setIsSectionLoading', true, { root: true });
      }
      promises.push(
        getGoalTeamsBySeason({
          seasonId: rootGetters['filterGeneral/getSeasonId'],
          teamId: state.team.team_id,
        }),
      );
      promises.push(dispatch('getTeamsBySeasonForAnalysis', true));
      promises.push(dispatch('playersGeneral/getPlayersBySeason', true, { root: true }));
      promises.push(
        dispatch('gamesGeneral/fetchGamesBySeason', rootGetters['filterGeneral/getSeasonId'], { root: true }),
      );
      promises.push(dispatch('leagueGeneral/getSeasonPlays', true, { root: true }));
      promises.push(dispatch('getTeamStatisticsBySeason', true));
      const [
        teamSeasons,
        {
          data: [{ data_combination }],
        },
        seasonTeams,
        seasonPlayers,
        seasonWithGames,
        seasonPlays,
        statistics,
      ] = await Promise.all(promises);
      const seasonGames = seasonWithGames.games || [];
      const objectTeamSeasons = {};
      if (teamSeasons) {
        const tournamentsForMediaPlayer = Object.entries(teamSeasons).reduce((accumulator, [key, value]) => {
          if (value.show_videos) {
            objectTeamSeasons[key] = value;
            const keyNumber = Number(key);
            const tournament = {
              _id: keyNumber,
              id: keyNumber,
              name: value.name,
              seasons: [
                {
                  id: keyNumber,
                  related_seasons: [],
                  type: value.type,
                },
              ],
            };
            accumulator.set(keyNumber, tournament);
          }
          return accumulator;
        }, new Map());
        dispatch('filterGeneral/updateSeasons', { seasons: objectTeamSeasons }, { root: true });
        commit('filterGeneral/setTournamentsForMediaPlayer', tournamentsForMediaPlayer, { root: true });
        commit('setFormattedSeasons', objectTeamSeasons);
        commit(
          'mediaGeneral/setSummaryData',
          { isTeam: true, subjectId: state.team.team_id, teamId: state.team.team_id },
          { root: true },
        );
      }
      if (seasonTeams) {
        const teamsMap = seasonTeams.reduce((accumulator, team) => {
          accumulator.set(team.team_id, team);
          return accumulator;
        }, new Map());
        commit('updateTeams', seasonTeams);
        commit('filterGeneral/setSeasonTeamsMap', teamsMap, { root: true });
        commit('updateTeamsForAnalysis', seasonTeams);
      }
      if (!state.team.team_name && state.team && state.team.team_id) {
        const currentTeam = state.seasonTeams.find(team => {
          return team.team_id === state.team.team_id;
        });
        commit('updateTeam', currentTeam);
      }
      if (seasonPlayers) {
        const currentTeamPlayers = seasonPlayers.find(item => item.team_id === state.team.team_id);
        commit('playersGeneral/updateCurrentTeamPlayers', currentTeamPlayers ? currentTeamPlayers.players : [], {
          root: true,
        });
        commit('playersGeneral/updateSeasonPlayers', seasonPlayers, { root: true });
      }
      if (seasonGames) {
        const gamesMap = seasonGames.reduce((accumulator, game) => {
          game.seasonType = seasonWithGames.season_type || 0;
          game.dateTimeMillis = new Date(game.date_time_utc).valueOf();
          accumulator.set(game.game_id, game);
          return accumulator;
        }, new Map());
        commit('filterGeneral/setSeasonGamesMap', gamesMap, { root: true });
        commit('gamesGeneral/updateSeasonGames', seasonGames, { root: true });
      }
      commit('leagueGeneral/updateSeasonPlays', seasonPlays, { root: true });
      commit('teamsPosession/updatePlaysStack', statistics.posession, { root: true });
      commit('teamsOffensive/updatePlaysStack', statistics.offensive, { root: true });
      commit('teamsDefensive/updatePlaysStack', statistics.defensive, { root: true });
      const { offensive, defensive } = filterByCategoryType(data_combination, teamGoalPlayIndexes());
      commit('teamsOffensive/updateGoalPlaysStack', offensive, { root: true });
      commit('teamsDefensive/updateGoalPlaysStack', defensive, { root: true });
      commit('setIsSectionLoading', false, { root: true });
      await dispatch('createPlaysByPlayers', { teamStatistics: statistics });
    } catch (e) {
      // commit('setIsSectionLoading', false, { root: true });
      return Promise.reject(e);
    }
  },

  createPlaysByPlayers({ state, commit }, { teamStatistics: { posession, offensive, defensive } }) {
    const basePlays = [...posession, ...offensive, ...defensive];
    const teamPlaysByPlayer = basePlays.reduce((accumulator, play) => {
      if (play[teamPlayIndexes.teamId] === state.team.team_id) {
        if (accumulator.has(play[teamPlayIndexes.playerId])) {
          accumulator.get(play[teamPlayIndexes.playerId]).push(play);
        } else {
          accumulator.set(play[teamPlayIndexes.playerId], [play]);
        }
      }
      return accumulator;
    }, new Map());
    commit('setTeamPlaysByPlayer', teamPlaysByPlayer);
  },

  /**
   * @author Alejandro
   * @param {Object} context
   * @param {boolean} holdLoading
   * @return {Promise}
   */
  async getTeamsBySeason({ commit, rootState }, seasonId, holdLoading) {
    try {
      if (!rootState.isSectionLoading) commit('setIsSectionLoading', true, { root: true });
      const result = await getTeamsBySeason({ seasonId });
      commit('updateTeams', result.teams);
      if (!holdLoading) commit('setIsSectionLoading', false, { root: true });
    } catch (e) {
      // commit('setIsSectionLoading', false, { root: true });
      return Promise.reject(e);
    }
  },

  /**
   * @author Alejandro
   * @param {Object} context
   * @param {boolean} returnData
   * @return {Promise}
   */
  async getTeamsBySeasonForAnalysis({ commit, rootGetters }, returnData = false) {
    try {
      // const selectedSeason = window.localStorage.getItem('seasonSelected');
      const seasonId = /*selectedSeason ? Number(selectedSeason) : */ rootGetters['filterGeneral/getSeasonId'];
      // if (state.lastSeasonForSeasonTeamsAnalysis === seasonId) {
      //   return undefined;
      // }
      const { teams } = await getTeamsBySeason({ seasonId });
      commit('updateLastSeasonForSeasonTeamsAnalysis', seasonId);
      if (returnData) {
        return teams;
      }
      commit('updateTeamsForAnalysis', teams);
    } catch (e) {
      return Promise.reject(e);
    }
  },

  /**
   * @author Alejandro
   * @param {Object} context
   */
  async getTeamStatisticsBySeason({ commit, rootGetters }, returnData = false) {
    try {
      const { data } = await getTeamStatisticsBySeason({
        seasonId: rootGetters['filterGeneral/getSeasonId'],
        teamId: state.team.team_id,
      });
      if (returnData) {
        return data.data;
      }
      commit('teamsPosession/updatePlaysStack', data.data.posession, { root: true });
      commit('teamsOffensive/updatePlaysStack', data.data.offensive, { root: true });
      commit('teamsDefensive/updatePlaysStack', data.data.defensive, { root: true });
    } catch (e) {
      return Promise.reject(e);
    }
  },

  async fetchGamesBySeason({ commit, rootGetters }) {
    const seasonId = rootGetters['filterGeneral/getSeasonId'];
    const url = `https://8tvo5bp9ai.execute-api.us-west-2.amazonaws.com/prod/gamesBySeason/${seasonId}/`;
    const result = await axios.get(url);
    commit('updateGames', result.data.games);
  },
  /**
   * @author Cristhian Gomez
   * @description This action cleans teams
   * @return {void}
   */
  cleanData({ commit }) {
    commit('teamsPosession/updatePlaysStack', [], { root: true });
  },
};

const getters = {
  team: state => state.team,
  teams: state => state.seasonTeams,
  teamId: state => state.team.team_id,
  teamsForAnalysis: state => {
    return state.seasonTeamsAnalysis;
  },
  teamSeasons(state) {
    if (state.teamSeasons && state.teamSeasons.seasons) {
      return Object.entries(state.teamSeasons.seasons).map(entry => {
        return {
          id: entry[0],
          name: entry[1],
        };
      });
    }
    return [];
  },
  teamSeasonPoints(state, getters, rootState, rootGetters) {
    const games = rootGetters['gamesGeneral/seasonGamesPlayed'];
    let points = 0;
    if (games) {
      games.forEach(game => {
        if (state.team.team_id === game.home_team) {
          if (game.score[0] - game.score[1] > 0) {
            points += 3;
          }
          if (game.score[0] - game.score[1] === 0) {
            points += 1;
          }
        }
        if (state.team.team_id === game.visiting_team) {
          if (game.score[1] - game.score[0] > 0) {
            points += 3;
          }
          if (game.score[1] - game.score[0] === 0) {
            points += 1;
          }
        }
      });
    }
    return points;
  },
  seasonTeams(state) {
    return state.seasonTeams;
  },
  /**
   * @author José Rafael Gutierrez
   * @param state
   * @param rootState
   * @param rootGetters
   * @return Array
   */
  getPlays: (state, rootState, rootGetters) => {
    const sections = rootGetters.filterGeneral.sections;
    const results = rootGetters.filterGeneral.results;
    const playingAs = rootGetters.filterGeneral.playingAs;
    const categoryType = rootGetters.filterGeneral.categoryType;
    return state.plays.filter(play => {
      return (
        play[5] === categoryType &&
        sections.includes(play[7]) &&
        results.includes(play[8]) &&
        (playingAs === null ? true : play[9] === playingAs)
      );
    });
  },
  getGamesBySeason(state) {
    return state.gamesBySeason;
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
